import React, { useState } from "react";
import { Modal, Card, Form, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import ForgetPassword from "./ForgetPassword";
import { customerLoginApi } from "../CustomerCRUD";

// ✅ Validation schema
const schema = Yup.object({
  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Min 6 chars")
    .max(12, "Max 12 chars"),
});

const LoginPage = ({ show, onHide }) => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [showForgetPassword, setShowForgetPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    setValue,
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // only numbers
    if (value.length <= 10) {
      setMobile(value);
      setValue("mobileNumber", value, { shouldValidate: true });
    } else {
      toast.error("Mobile number cannot exceed 10 digits");
    }
    clearErrors("root");
  };

  const onSubmit = async ({ mobileNumber, password }) => {
    try {
      const response = await customerLoginApi(mobileNumber, password);
      if (response.data?.isSuccess) {
        localStorage.setItem("customer", JSON.stringify(response.data.data));
        onHide(); // close modal on success
        navigate("/checkout"); // redirect
      } else {
        setError("root", {
          type: "manual",
          message: response.data?.message || "Login failed.",
        });
      }
    } catch (err) {
      setError("root", { type: "manual", message: err.message });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        {/* <Modal.Title>Customer Login</Modal.Title> */}
      </Modal.Header>
      <Modal.Body
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="p-4 bg-light" style={{ width: "400px" }}>
          <h6 className="text-center mb-4" style={{ color: "#4c934c" }}>
            Login
          </h6>

          {errors.root?.message && (
            <Alert variant="danger">{errors.root.message}</Alert>
          )}

          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* ✅ Mobile Input */}
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={handleMobileChange}
                isInvalid={!!errors.mobileNumber}
              />
              <Form.Control.Feedback type="invalid">
                {errors.mobileNumber?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {/* ✅ Password Input */}
            <Form.Group className="mb-3">
              <Form.Label>
                Password{" "}
                <span
                  className="hover:underline text-primary"
                  onClick={() => setShowForgetPassword(true)}
                >
                  Forgot Password
                </span>
              </Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                {...register("password", {
                  onChange: () => clearErrors("root"),
                })}
                isInvalid={!!errors.password}
              />
              <Form.Control.Feedback type="invalid">
                {errors.password?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {/* ✅ Submit */}
            <Button
              type="submit"
              variant="danger"
              className="w-100"
              style={{ backgroundColor: "#4c934c", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing in..." : "Login"}
            </Button>
          </Form>

          <p className="text-center mt-3">
            Don't have an account?{" "}
            <a href="/register" className="hover:underline text-primary">
              Register
            </a>
          </p>
          <ForgetPassword
            show={showForgetPassword}
            onHide={() => setShowForgetPassword(false)}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LoginPage;
